/**
 * Chấm điểm SEO cho một bài blog, chạy ngay trên trình duyệt trong lúc người viết soạn.
 *
 * Bám theo các tiêu chí phổ biến của Rank Math và Yoast: độ dài tiêu đề và mô tả theo đúng phần Google hiển
 * thị, từ khóa chính có mặt ở những chỗ công cụ tìm kiếm coi trọng, độ dài và cấu trúc bài, ảnh có mô tả,
 * có liên kết. Đây là gợi ý cho người viết — không có tiêu chí nào chặn việc đăng bài.
 */

/** Mức của một tiêu chí: đạt, nên sửa, hoặc thiếu hẳn. */
export type SeoStatus = 'good' | 'warning' | 'bad';

/** Kết quả một tiêu chí. */
export interface SeoCheck {
  readonly key: string;
  readonly status: SeoStatus;
  /** Câu ngắn nói tiêu chí đang ở tình trạng nào. */
  readonly title: string;
  /** Vì sao tiêu chí này quan trọng, hoặc cách sửa. */
  readonly detail: string;
}

/** Dữ liệu đầu vào — lấy thẳng từ biểu mẫu soạn bài. */
export interface SeoInput {
  readonly title: string;
  readonly seoTitle: string;
  readonly metaDescription: string;
  readonly excerpt: string;
  /** Đường dẫn thực tế của bài: đã gõ, hoặc sinh từ tiêu đề. */
  readonly slug: string;
  readonly focusKeyword: string;
  readonly contentHtml: string;
  readonly hasCoverImage: boolean;
  readonly coverImageAlt: string;
}

/** Kết quả chấm điểm. */
export interface SeoReport {
  readonly checks: readonly SeoCheck[];
  /** Điểm từ 0 tới 100. */
  readonly score: number;
}

/** Khoảng độ dài tiêu đề Google hiển thị trọn. */
export const seoTitleRange = { min: 30, max: 60 } as const;

/** Khoảng độ dài mô tả Google hiển thị trọn. */
export const metaDescriptionRange = { min: 120, max: 160 } as const;

/** Số chữ để bài được coi là đủ sâu. */
const recommendedWords = 600;

/** Dưới mức này bài bị coi là quá mỏng. */
const minimumWords = 300;

/** Mật độ từ khóa hợp lý, tính theo phần trăm số chữ. */
const densityRange = { min: 0.5, max: 2.5 } as const;

/** Đường dẫn dài hơn mức này khó đọc và hay bị cắt trên kết quả tìm kiếm. */
const maximumSlugLength = 75;

/** Tên miền của website — liên kết về đây được tính là liên kết nội bộ. */
const websiteHost = 'mvalab.vn';

/**
 * Chuẩn hóa một đoạn chữ thành đường dẫn — cùng cách với máy chủ: bỏ dấu, đ → d, chữ thường, ký tự khác
 * thành gạch ngang.
 */
export function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/đ/g, 'd')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120)
    .replace(/-+$/g, '');
}

/** So khớp không phân biệt hoa thường, giữ dấu tiếng Việt — người Việt tìm kiếm có dấu. */
function normalize(text: string): string {
  return text.normalize('NFC').toLowerCase().replace(/\s+/g, ' ').trim();
}

function countWords(text: string): number {
  return text.split(/\s+/).filter((word) => word !== '').length;
}

function countOccurrences(haystack: string, needle: string): number {
  if (needle === '') {
    return 0;
  }

  let count = 0;
  let index = haystack.indexOf(needle);

  while (index !== -1) {
    count++;
    index = haystack.indexOf(needle, index + needle.length);
  }

  return count;
}

/** Những gì cần biết về nội dung bài để chấm điểm. */
interface ContentFacts {
  readonly text: string;
  readonly wordCount: number;
  readonly headings: readonly string[];
  readonly h2Count: number;
  readonly firstParagraph: string;
  readonly imageCount: number;
  readonly imagesWithoutAlt: number;
  readonly internalLinks: number;
  readonly externalLinks: number;
}

function analyzeContent(html: string): ContentFacts {
  const document = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const body = document.body;
  const text = body.textContent;

  const headings = [...body.querySelectorAll('h2, h3, h4')].map((heading) => heading.textContent);
  const firstParagraph =
    [...body.querySelectorAll('p')].map((paragraph) => paragraph.textContent).find((value) => value.trim() !== '') ?? '';

  const images = [...body.querySelectorAll('img')];

  let internalLinks = 0;
  let externalLinks = 0;

  for (const link of body.querySelectorAll('a[href]')) {
    try {
      const url = new URL(link.getAttribute('href') ?? '', `https://${websiteHost}`);

      if (url.hostname === websiteHost || url.hostname.endsWith(`.${websiteHost}`)) {
        internalLinks++;
      } else if (url.protocol === 'http:' || url.protocol === 'https:') {
        externalLinks++;
      }
    } catch {
      // Địa chỉ hỏng — không tính.
    }
  }

  return {
    text,
    wordCount: countWords(text),
    headings,
    h2Count: body.querySelectorAll('h2').length,
    firstParagraph,
    imageCount: images.length,
    imagesWithoutAlt: images.filter((image) => (image.getAttribute('alt') ?? '').trim() === '').length,
    internalLinks,
    externalLinks,
  };
}

function lengthCheck(
  key: string,
  label: string,
  value: string,
  range: { readonly min: number; readonly max: number },
  why: string,
): SeoCheck {
  const length = value.trim().length;
  const span = `${range.min.toString()}–${range.max.toString()} ký tự`;

  if (length === 0) {
    return { key, status: 'bad', title: `Chưa có ${label.toLowerCase()}`, detail: why };
  }

  if (length < range.min) {
    return { key, status: 'warning', title: `${label} hơi ngắn (${length.toString()} ký tự)`, detail: `Nên dài ${span}. ${why}` };
  }

  if (length > range.max) {
    return {
      key,
      status: 'warning',
      title: `${label} dài quá (${length.toString()} ký tự)`,
      detail: `Google sẽ cắt phần sau ký tự thứ ${range.max.toString()}. Nên dài ${span}.`,
    };
  }

  return { key, status: 'good', title: `${label} dài vừa đẹp (${length.toString()} ký tự)`, detail: why };
}

function keywordChecks(
  input: SeoInput,
  facts: ContentFacts,
  keyword: string,
  effectiveTitle: string,
  effectiveMeta: string,
): SeoCheck[] {
  const checks: SeoCheck[] = [];
  const keywordSlug = slugify(keyword);

  checks.push(
    normalize(effectiveTitle).includes(keyword)
      ? { key: 'keyword-title', status: 'good', title: 'Từ khóa có trong tiêu đề SEO', detail: 'Tiêu đề là tín hiệu mạnh nhất với Google.' }
      : { key: 'keyword-title', status: 'bad', title: 'Từ khóa chưa có trong tiêu đề SEO', detail: 'Đưa từ khóa chính vào tiêu đề, càng gần đầu càng tốt.' },
  );

  checks.push(
    normalize(effectiveMeta).includes(keyword)
      ? { key: 'keyword-meta', status: 'good', title: 'Từ khóa có trong mô tả SEO', detail: 'Google in đậm từ khóa trong mô tả, giúp người tìm chú ý.' }
      : { key: 'keyword-meta', status: 'warning', title: 'Từ khóa chưa có trong mô tả SEO', detail: 'Viết lại mô tả có chứa từ khóa chính.' },
  );

  checks.push(
    keywordSlug !== '' && input.slug.includes(keywordSlug)
      ? { key: 'keyword-slug', status: 'good', title: 'Từ khóa có trong đường dẫn', detail: `Đường dẫn chứa "${keywordSlug}".` }
      : {
          key: 'keyword-slug',
          status: 'warning',
          title: 'Từ khóa chưa có trong đường dẫn',
          detail: `Nên có "${keywordSlug}" trong đường dẫn — sửa trước khi đăng, đăng rồi mới đổi sẽ gãy link đã chia sẻ.`,
        },
  );

  checks.push(
    normalize(facts.firstParagraph).includes(keyword)
      ? { key: 'keyword-intro', status: 'good', title: 'Từ khóa có trong đoạn mở đầu', detail: 'Người đọc và Google biết ngay bài nói về gì.' }
      : { key: 'keyword-intro', status: 'warning', title: 'Từ khóa chưa có trong đoạn mở đầu', detail: 'Nhắc từ khóa chính trong đoạn văn đầu tiên.' },
  );

  checks.push(
    facts.headings.some((heading) => normalize(heading).includes(keyword))
      ? { key: 'keyword-heading', status: 'good', title: 'Từ khóa có trong tiêu đề mục', detail: 'Ít nhất một tiêu đề mục chứa từ khóa.' }
      : { key: 'keyword-heading', status: 'warning', title: 'Từ khóa chưa có trong tiêu đề mục nào', detail: 'Đặt từ khóa vào ít nhất một tiêu đề lớn hoặc vừa.' },
  );

  const occurrences = countOccurrences(normalize(facts.text), keyword);
  const density = facts.wordCount === 0 ? 0 : ((occurrences * countWords(keyword)) / facts.wordCount) * 100;
  const densityText = `${density.toLocaleString('vi-VN', { maximumFractionDigits: 1 })}% (${occurrences.toString()} lần)`;

  if (occurrences === 0) {
    checks.push({ key: 'keyword-density', status: 'bad', title: 'Từ khóa không xuất hiện trong nội dung', detail: 'Dùng từ khóa chính tự nhiên vài lần trong bài.' });
  } else if (density < densityRange.min) {
    checks.push({ key: 'keyword-density', status: 'warning', title: `Từ khóa xuất hiện ít: ${densityText}`, detail: 'Nên khoảng 0,5–2,5% số chữ.' });
  } else if (density > densityRange.max) {
    checks.push({ key: 'keyword-density', status: 'warning', title: `Từ khóa dày quá: ${densityText}`, detail: 'Google coi là nhồi từ khóa. Nên khoảng 0,5–2,5% số chữ.' });
  } else {
    checks.push({ key: 'keyword-density', status: 'good', title: `Mật độ từ khóa hợp lý: ${densityText}`, detail: 'Trong khoảng 0,5–2,5% số chữ.' });
  }

  return checks;
}

/**
 * Chấm điểm một bài.
 *
 * Điểm = (số tiêu chí đạt + nửa số tiêu chí nên sửa) / tổng số tiêu chí. Chưa đặt từ khóa chính thì các tiêu
 * chí về từ khóa không được chấm — chỉ có một lời nhắc đặt từ khóa.
 */
export function runSeoChecks(input: SeoInput): SeoReport {
  const facts = analyzeContent(input.contentHtml);
  const effectiveTitle = input.seoTitle.trim() === '' ? input.title : input.seoTitle;
  const effectiveMeta = input.metaDescription.trim() === '' ? input.excerpt : input.metaDescription;
  const keyword = normalize(input.focusKeyword);

  const checks: SeoCheck[] = [
    lengthCheck('title-length', 'Tiêu đề SEO', effectiveTitle, seoTitleRange, 'Dòng chữ xanh to nhất trên kết quả tìm kiếm Google.'),
    lengthCheck('meta-length', 'Mô tả SEO', effectiveMeta, metaDescriptionRange, 'Đoạn chữ xám dưới tiêu đề trên Google — quyết định người tìm có bấm vào không.'),
  ];

  if (keyword === '') {
    checks.push({ key: 'keyword-set', status: 'warning', title: 'Chưa đặt từ khóa chính', detail: 'Đặt cụm từ khách hay gõ trên Google để được chấm các tiêu chí về từ khóa.' });
  } else {
    checks.push(...keywordChecks(input, facts, keyword, effectiveTitle, effectiveMeta));
  }

  const words = facts.wordCount.toLocaleString('vi-VN');

  checks.push(
    facts.wordCount >= recommendedWords
      ? { key: 'content-length', status: 'good', title: `Bài đủ sâu: ${words} chữ`, detail: 'Từ 600 chữ trở lên.' }
      : facts.wordCount >= minimumWords
        ? { key: 'content-length', status: 'warning', title: `Bài hơi ngắn: ${words} chữ`, detail: 'Bài từ 600 chữ thường xếp hạng tốt hơn.' }
        : { key: 'content-length', status: 'bad', title: `Bài quá ngắn: ${words} chữ`, detail: 'Dưới 300 chữ, Google coi là nội dung mỏng.' },
  );

  checks.push(
    facts.h2Count > 0
      ? { key: 'headings', status: 'good', title: `Có ${facts.h2Count.toString()} tiêu đề mục lớn`, detail: 'Bài chia mục rõ ràng, dễ đọc lướt.' }
      : { key: 'headings', status: 'warning', title: 'Chưa có tiêu đề mục', detail: 'Chia bài thành các mục bằng "Tiêu đề lớn" trên thanh công cụ.' },
  );

  if (!input.hasCoverImage) {
    checks.push({ key: 'cover', status: 'bad', title: 'Chưa có ảnh bìa', detail: 'Chia sẻ lên Facebook, Zalo sẽ không có ảnh — ít người bấm hơn hẳn.' });
  } else if (input.coverImageAlt.trim() === '') {
    checks.push({ key: 'cover', status: 'warning', title: 'Ảnh bìa chưa có mô tả (alt)', detail: 'Viết mô tả ảnh bìa — Google Hình ảnh đọc phần này.' });
  } else {
    checks.push({ key: 'cover', status: 'good', title: 'Có ảnh bìa kèm mô tả', detail: 'Hiện đẹp khi chia sẻ và được Google Hình ảnh đọc.' });
  }

  if (facts.imageCount === 0) {
    checks.push({ key: 'images', status: 'warning', title: 'Trong bài chưa có ảnh', detail: 'Thêm ít nhất một ảnh minh họa giúp bài dễ đọc hơn.' });
  } else if (facts.imagesWithoutAlt > 0) {
    checks.push({
      key: 'images',
      status: 'warning',
      title: `${facts.imagesWithoutAlt.toString()}/${facts.imageCount.toString()} ảnh chưa có mô tả`,
      detail: 'Bấm vào ảnh rồi bấm nút "Mô tả ảnh" trên thanh công cụ.',
    });
  } else {
    checks.push({ key: 'images', status: 'good', title: `Cả ${facts.imageCount.toString()} ảnh đều có mô tả`, detail: 'Tốt cho Google Hình ảnh và người dùng trình đọc màn hình.' });
  }

  checks.push(
    facts.internalLinks > 0
      ? { key: 'internal-links', status: 'good', title: `Có ${facts.internalLinks.toString()} liên kết nội bộ`, detail: 'Liên kết về trang khác của mvalab.vn giữ người đọc ở lại website.' }
      : { key: 'internal-links', status: 'warning', title: 'Chưa có liên kết nội bộ', detail: 'Thêm link tới một trang khác của mvalab.vn (giải pháp, dự án, bài liên quan).' },
  );

  checks.push(
    facts.externalLinks > 0
      ? { key: 'external-links', status: 'good', title: `Có ${facts.externalLinks.toString()} liên kết ra ngoài`, detail: 'Dẫn nguồn uy tín giúp bài đáng tin hơn.' }
      : { key: 'external-links', status: 'warning', title: 'Chưa có liên kết ra nguồn ngoài', detail: 'Dẫn ít nhất một nguồn uy tín (tài liệu hãng, tiêu chuẩn, nghiên cứu).' },
  );

  checks.push(
    input.slug.length <= maximumSlugLength
      ? { key: 'slug-length', status: 'good', title: 'Đường dẫn gọn', detail: `${input.slug.length.toString()} ký tự.` }
      : { key: 'slug-length', status: 'warning', title: `Đường dẫn dài (${input.slug.length.toString()} ký tự)`, detail: 'Rút gọn còn dưới 75 ký tự, chỉ giữ các chữ chính.' },
  );

  const points = checks.reduce(
    (sum, check) => sum + (check.status === 'good' ? 1 : check.status === 'warning' ? 0.5 : 0),
    0,
  );

  return { checks, score: Math.round((points / checks.length) * 100) };
}
